import { Box, Button, DatePicker, LegacyStack, Popover, Range, TextField } from '@shopify/polaris'
import { CalendarIcon } from '@shopify/polaris-icons'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useState } from 'react'

export const dateRex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/

const buildMonthDate = (date?: string) => {
  const dateObj = date ? new Date(date) : new Date()

  return {
    month: dateObj.getMonth(),
    year: dateObj.getFullYear(),
  }
}

const formatter = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const getDateMonth = (date?: string) => {
  const dateObj = date ? new Date(date) : new Date()
  return dateObj.getMonth()
}

const getDateYear = (date?: string) => {
  const dateObj = date ? new Date(date) : new Date()
  return dateObj.getFullYear()
}

const DatePickerPopover = ({ date, onSelect }: { date: string; onSelect: (date: Date) => any }) => {
  const [month, setMonth] = useState<number>(getDateMonth(date))
  const [year, setYear] = useState<number>(getDateYear(date))

  const updateMonthDate = useCallback((month: number, year: number) => {
    setMonth(month)
    setYear(year)
  }, [])

  const [popoverActive, setPopoverActive] = useState(false)
  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    []
  )

  useEffect(() => {
    setMonth(getDateMonth(date))
    setYear(getDateYear(date))
  }, [date])

  return (
    <Popover
      active={popoverActive}
      activator={<Button icon={CalendarIcon} onClick={togglePopoverActive} />}
      onClose={togglePopoverActive}
      ariaHaspopup={false}
      sectioned
    >
      <DatePicker
        month={month}
        year={year}
        onMonthChange={updateMonthDate}
        onChange={(range: Range) => {
          onSelect(range.start)
          togglePopoverActive()
        }}
        selected={new Date(date)}
      />
    </Popover>
  )
}

export const DatePickerWithInput = ({
  label,
  date,
  updateDate,
  setError,
  datePickerPopover,
}: {
  label: React.ReactNode
  date?: string
  updateDate: (date?: string) => void
  setError?: (error: string) => void
  datePickerPopover?: boolean
}) => {
  const [monthDate, setMonthDate] = useState<{ month: number; year: number }>(buildMonthDate(date))
  const updateMonthDate = useCallback(
    (month: number, year: number) => setMonthDate({ month, year }),
    []
  )

  const [dateInput, setDateInput] = useState(date ?? '')
  const [dateError, setDateError] = useState('')

  useEffect(() => {
    if (setError) {
      setError(dateError)
    }
  }, [dateError, setError])

  const handleDateInputBlur = useCallback(() => {
    if (dateInput !== '' && !dateRex.test(dateInput)) {
      setDateError('Must match YYYY-MM-DD format')
    } else {
      setDateError('')
      updateDate(dateInput === '' ? undefined : dateInput)
      setMonthDate(buildMonthDate(dateInput))
    }
  }, [dateInput, setDateError, updateDate])

  const handleSelectedRange = useCallback(
    (range: Range) => {
      const dateStr = DateTime.fromJSDate(range.start).toISODate()
      updateDate(dateStr)
      setDateInput(dateStr)
    },
    [updateDate, setDateInput]
  )

  const handleSelectedDate = useCallback(
    (date: Date) => {
      setDateError('')
      updateDate(formatter.format(date))
      setDateInput(formatter.format(date))
    },
    [updateDate, setDateInput]
  )

  return (
    <Box width="250px">
      <LegacyStack vertical>
        <TextField
          value={dateInput}
          placeholder="YYYY-MM-DD"
          label={label}
          autoComplete="off"
          onChange={setDateInput}
          onBlur={handleDateInputBlur}
          error={dateError}
          connectedRight={
            datePickerPopover && (
              <DatePickerPopover
                date={date ?? new Date().toISOString()}
                onSelect={handleSelectedDate}
              />
            )
          }
        />
        {!datePickerPopover && (
          <DatePicker
            month={monthDate.month}
            year={monthDate.year}
            onMonthChange={updateMonthDate}
            onChange={handleSelectedRange}
            selected={date ? new Date(date) : undefined}
          />
        )}
      </LegacyStack>
    </Box>
  )
}
